import React, { useCallback, useEffect, useState } from 'react';
import {
  listRetakeFeesApi, upsertRetakeFeeApi, deleteRetakeFeeApi,
  listBankAccountsApi, createBankAccountApi, updateBankAccountApi, deleteBankAccountApi,
  type RetakeFee, type BankAccount,
} from '../../../features/chatbot/chatbotApi';
import './RetakeFeeManager.scss';


const PART_CODES = [
  { code: 'L',    name: 'Lý thuyết' },
  { code: 'M',    name: 'Mô phỏng'  },
  { code: 'H',    name: 'Hình'       },
  { code: 'D',    name: 'Đường'      },
  { code: 'GPLX', name: 'Phí GPLX'  },
];

const VEHICLE_TYPES = [
  { code: 'moto', name: 'Mô tô' },
  { code: 'oto',  name: 'Ô tô'  },
];

type FeeMap = Record<string, Record<string, { id: number; amount: number }>>;

const buildFeeMap = (fees: RetakeFee[]): FeeMap => {
  const map: FeeMap = {};
  for (const f of fees) {
    if (!map[f.vehicleType]) map[f.vehicleType] = {};
    map[f.vehicleType][f.partCode] = { id: f.id, amount: f.amount };
  }
  return map;
};

type BankFormState = {
  bankCode: string;
  bankName: string;
  accountNumber: string;
  accountHolder: string;
  isDefault: boolean;
};

const EMPTY_BANK: BankFormState = { bankCode: '', bankName: '', accountNumber: '', accountHolder: '', isDefault: false };

const RetakeFeeManager: React.FC = () => {
  const [fees, setFees] = useState<RetakeFee[]>([]);
  const [accounts, setAccounts] = useState<BankAccount[]>([]);
  const [feeMap, setFeeMap] = useState<FeeMap>({});
  const [saving, setSaving] = useState(false);
  const [editCell, setEditCell] = useState<{ vehicleType: string; partCode: string; value: string } | null>(null);

  const [bankForm, setBankForm] = useState<BankFormState>(EMPTY_BANK);
  const [editBankId, setEditBankId] = useState<number | null>(null);
  const [bankSaving, setBankSaving] = useState(false);

  const loadData = useCallback(async () => {
    const [feeRes, bankRes] = await Promise.all([listRetakeFeesApi(), listBankAccountsApi()]);
    if (feeRes.EC === 0) {
      setFees(feeRes.DT);
      setFeeMap(buildFeeMap(feeRes.DT));
    }
    if (bankRes.EC === 0) setAccounts(bankRes.DT);
  }, []);

  useEffect(() => { void loadData(); }, [loadData]);

  const cellAmount = (vehicleType: string, partCode: string) =>
    feeMap[vehicleType]?.[partCode]?.amount ?? 0;

  const startEdit = (vehicleType: string, partCode: string) => {
    setEditCell({ vehicleType, partCode, value: String(cellAmount(vehicleType, partCode) || '') });
  };

  const commitEdit = async () => {
    if (!editCell) return;
    const amount = parseInt(editCell.value.replace(/\D/g, ''), 10) || 0;
    const part = PART_CODES.find((p) => p.code === editCell.partCode);
    setSaving(true);
    try {
      await upsertRetakeFeeApi({ vehicleType: editCell.vehicleType, partCode: editCell.partCode, partName: part?.name || editCell.partCode, amount });
      setEditCell(null);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const removeCell = async (vehicleType: string, partCode: string) => {
    const existing = feeMap[vehicleType]?.[partCode];
    if (!existing) return;
    setSaving(true);
    try {
      await deleteRetakeFeeApi(existing.id);
      await loadData();
    } finally {
      setSaving(false);
    }
  };

  const startEditBank = (acc: BankAccount) => {
    setEditBankId(acc.id);
    setBankForm({ bankCode: acc.bankCode, bankName: acc.bankName, accountNumber: acc.accountNumber, accountHolder: acc.accountHolder, isDefault: acc.isDefault });
  };

  const cancelEditBank = () => {
    setEditBankId(null);
    setBankForm(EMPTY_BANK);
  };

  const saveBank = async () => {
    if (!bankForm.bankCode || !bankForm.bankName || !bankForm.accountNumber || !bankForm.accountHolder) return;
    setBankSaving(true);
    try {
      if (editBankId != null) {
        await updateBankAccountApi(editBankId, bankForm);
      } else {
        await createBankAccountApi(bankForm);
      }
      setBankForm(EMPTY_BANK);
      setEditBankId(null);
      await loadData();
    } finally {
      setBankSaving(false);
    }
  };

  const deleteBank = async (id: number) => {
    if (!window.confirm('Xóa tài khoản này?')) return;
    setBankSaving(true);
    try {
      await deleteBankAccountApi(id);
      await loadData();
    } finally {
      setBankSaving(false);
    }
  };

  const setDefault = async (id: number) => {
    const acc = accounts.find((a) => a.id === id);
    if (!acc) return;
    setBankSaving(true);
    try {
      await updateBankAccountApi(id, { ...acc, isDefault: true });
      await loadData();
    } finally {
      setBankSaving(false);
    }
  };

  return (
    <div className="rfm">
      <h2 className="rfm__title">Bảng phí thi lại & Tài khoản ngân hàng</h2>

      {/* ── Fee matrix ── */}
      <section className="rfm__section">
        <h3 className="rfm__section-title">
          <i className="material-icons">receipt_long</i>
          Bảng phí thi lại (theo loại xe × phần thi)
        </h3>
        <p className="rfm__section-hint">Click vào ô để nhập / sửa phí. Để 0 hoặc trống = miễn phí.</p>
        <div className="rfm__table-wrap">
          <table className="rfm__table">
            <thead>
              <tr>
                <th className="rfm__th rfm__th--hang">Loại xe</th>
                {PART_CODES.map((p) => (
                  <th key={p.code} className="rfm__th">{p.name} ({p.code})</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {VEHICLE_TYPES.map((vt) => (
                <tr key={vt.code}>
                  <td className="rfm__td rfm__td--hang">{vt.name}</td>
                  {PART_CODES.map((p) => {
                    const isEditing = editCell?.vehicleType === vt.code && editCell.partCode === p.code;
                    const amt = cellAmount(vt.code, p.code);
                    return (
                      <td key={p.code} className="rfm__td rfm__td--amount">
                        {isEditing ? (
                          <div className="rfm__cell-edit">
                            <input
                              className="rfm__cell-input"
                              type="number"
                              min={0}
                              value={editCell.value}
                              onChange={(e) => setEditCell({ ...editCell, value: e.target.value })}
                              onKeyDown={(e) => { if (e.key === 'Enter') void commitEdit(); if (e.key === 'Escape') setEditCell(null); }}
                              autoFocus
                              disabled={saving}
                            />
                            <button className="rfm__cell-save" onClick={() => void commitEdit()} disabled={saving}>
                              <i className="material-icons">check</i>
                            </button>
                            <button className="rfm__cell-cancel" onClick={() => setEditCell(null)}>
                              <i className="material-icons">close</i>
                            </button>
                          </div>
                        ) : (
                          <div className="rfm__cell-view" onClick={() => startEdit(vt.code, p.code)}>
                            <span className={amt ? 'rfm__cell-value' : 'rfm__cell-empty'}>
                              {amt ? amt.toLocaleString('vi-VN') : '—'}
                            </span>
                            {amt > 0 && (
                              <button
                                className="rfm__cell-del"
                                title="Xóa"
                                onClick={(e) => { e.stopPropagation(); void removeCell(vt.code, p.code); }}
                                disabled={saving}
                              >
                                <i className="material-icons">delete</i>
                              </button>
                            )}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Bank accounts ── */}
      <section className="rfm__section">
        <h3 className="rfm__section-title">
          <i className="material-icons">account_balance</i>
          Tài khoản ngân hàng (VietQR)
        </h3>

        <div className="rfm__bank-list">
          {accounts.map((acc) => (
            <div key={acc.id} className={`rfm__bank-card${acc.isDefault ? ' rfm__bank-card--default' : ''}`}>
              <div className="rfm__bank-card-info">
                <span className="rfm__bank-name">{acc.bankName}</span>
                <span className="rfm__bank-number">{acc.accountNumber}</span>
                <span className="rfm__bank-holder">{acc.accountHolder}</span>
                {acc.isDefault && <span className="rfm__bank-badge">Mặc định</span>}
              </div>
              <div className="rfm__bank-card-actions">
                {!acc.isDefault && (
                  <button className="rfm__bank-btn" onClick={() => void setDefault(acc.id)} disabled={bankSaving} title="Đặt mặc định">
                    <i className="material-icons">star</i>
                  </button>
                )}
                <button className="rfm__bank-btn" onClick={() => startEditBank(acc)} disabled={bankSaving} title="Sửa">
                  <i className="material-icons">edit</i>
                </button>
                <button className="rfm__bank-btn rfm__bank-btn--del" onClick={() => void deleteBank(acc.id)} disabled={bankSaving} title="Xóa">
                  <i className="material-icons">delete</i>
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="rfm__bank-form">
          <h4 className="rfm__bank-form-title">{editBankId != null ? 'Sửa tài khoản' : 'Thêm tài khoản mới'}</h4>
          <div className="rfm__bank-form-row">
            <label className="rfm__label">
              Mã ngân hàng (VietQR)
              <input className="rfm__input" placeholder="VD: VCB, ACB, TCB…" value={bankForm.bankCode} onChange={(e) => setBankForm({ ...bankForm, bankCode: e.target.value.toUpperCase() })} />
            </label>
            <label className="rfm__label">
              Tên ngân hàng
              <input className="rfm__input" placeholder="VD: Vietcombank" value={bankForm.bankName} onChange={(e) => setBankForm({ ...bankForm, bankName: e.target.value })} />
            </label>
          </div>
          <div className="rfm__bank-form-row">
            <label className="rfm__label">
              Số tài khoản
              <input className="rfm__input" placeholder="0123456789" value={bankForm.accountNumber} onChange={(e) => setBankForm({ ...bankForm, accountNumber: e.target.value })} />
            </label>
            <label className="rfm__label">
              Chủ tài khoản
              <input className="rfm__input" placeholder="NGUYEN VAN A" value={bankForm.accountHolder} onChange={(e) => setBankForm({ ...bankForm, accountHolder: e.target.value.toUpperCase() })} />
            </label>
          </div>
          <label className="rfm__checkbox-label">
            <input type="checkbox" checked={bankForm.isDefault} onChange={(e) => setBankForm({ ...bankForm, isDefault: e.target.checked })} />
            Đặt làm tài khoản mặc định cho chatbot
          </label>
          <div className="rfm__bank-form-btns">
            <button className="rfm__btn rfm__btn--primary" onClick={() => void saveBank()} disabled={bankSaving || !bankForm.bankCode || !bankForm.accountNumber}>
              {bankSaving ? 'Đang lưu…' : editBankId != null ? 'Cập nhật' : 'Thêm tài khoản'}
            </button>
            {editBankId != null && (
              <button className="rfm__btn" onClick={cancelEditBank}>Hủy</button>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default RetakeFeeManager;
